import { Box, Grid, Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import product2IllustrationImg from '../../../../assets/products/product-2/product2Illustration.png';
import { localized, localizedInterpolation } from '../../../../i18n/i18n';
import { AppState } from '../../../../state/store';
import {
  getUnitLenghtByCulture,
  getValueLengthWithUnitByCulture,
} from '../../../../utilities/helpers/culture-settings-helper';
import { manuelNumberCheck } from '../../../../utilities/helpers/form-helper';
import { BinaryButtonInput } from '../../../components/custom-components/configuration/dynamic-components/binary-button-input';
import { ImageRow } from '../../../components/custom-components/configuration/dynamic-components/image-row';
import { PlacementWarningRow } from '../../../components/custom-components/configuration/dynamic-components/placement-warning-row';
import { ValueRangeInput } from '../../../components/custom-components/configuration/dynamic-components/value-range-input';
import { tableUseCase, worktopDepth, worktopLength } from '../configuration-constants';
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grid: {
      width: '686px',
      [theme.breakpoints.down('lg')]: {
        width: '500px',
      },
    },
    gridItem: {
      height: '140px',
    },
  }),
);

export const Product2Step1: FC = React.memo(() => {
  const classes = useStyles();
  const [showPlacementWarning, setShowPlacmentWarning] = useState<boolean>(false);
  const {unitsId} = useSelector((store: AppState) => store.urlSettingsReducer.cultureUrlParameters);
  const myRef = useRef<null | HTMLDivElement>(null);
  const formContext = useFormContext();

  useEffect(() => {
    if (myRef && myRef.current) {
      myRef.current.scrollIntoView({block: 'end'});
    }
  }, []);

  const useCaseBtnCallback = useCallback((value: boolean) => {
    setShowPlacmentWarning(!value);
  }, []);

  const between600And3000 = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '600'),
        getUnitLenghtByCulture(unitsId, '3000'),
        unitsId,
        formContext,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsId],
  );

  const between550And700 = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '550'),
        getUnitLenghtByCulture(unitsId, '700'),
        unitsId,
        formContext,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsId],
  );

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" sx={{flexGrow: 1}}>
        <Grid container spacing={2} className={classes.grid}>
          <Grid ref={myRef} container spacing={6}>
            <ImageRow imgString={product2IllustrationImg} />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
            <ValueRangeInput
              formName={worktopLength}
              minValue={'600'}
              maxValue={'3000'}
              textInputLabel={localized('LengthOfTheTableTop') + ' (A)'}
              tooltipLabel={localizedInterpolation('ToolTipProduct2Step1_1', {
                value1: getValueLengthWithUnitByCulture(unitsId, '600'),
                value2: getValueLengthWithUnitByCulture(unitsId, '3000'),
              })}
              formOnChange={between600And3000}
            />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
            <ValueRangeInput
              formName={worktopDepth}
              minValue={'550'}
              maxValue={'700'}
              textInputLabel={localized('DepthOfTheTableTop') + ' (B)'}
              tooltipLabel={localizedInterpolation('ToolTipProduct2Step1_2', {
                value1: getValueLengthWithUnitByCulture(unitsId, '550'),
                value2: getValueLengthWithUnitByCulture(unitsId, '700'),
              })}
              formOnChange={between550And700}
            />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
            <BinaryButtonInput
              formName={tableUseCase}
              value1={'true'}
              value2={'false'}
              btnLabel1={localized('Yes')}
              btnLabel2={localized('No')}
              label={localized('TabletopForWasingOrCooking')}
              tooltipLabel={localizedInterpolation('ToolTipProduct2Step1_3', {
                value1: getValueLengthWithUnitByCulture(unitsId, '70'),
                value2: getValueLengthWithUnitByCulture(unitsId, '500'),
              })}
              buttonPressedCallback={useCaseBtnCallback}
            />
          </Grid>

          <PlacementWarningRow showPlacementWarning={showPlacementWarning} />
        </Grid>
      </Box>
    </>
  );
});
